import React from "react";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import { get, map, debounce, size, filter } from "lodash";

import { fetchGet, fetchPost, fetchPut } from "../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import CreateForm from "./CreateForm";
import CreateDialog from "./CreateDialog";
import CustomButton from "../Common/CustomButton";

import { levels } from "../../constants/companies";
import { error, success } from "../../constants/common";

import { getLov } from "../../utils/lov";
import { getAccessibleCompanies } from "../../utils/companies";
import { setUser } from "../../actions/user";

const CreateContainer = (props) => {
  const {
    language,
    user,
    level,
    refresh,
    item,
    edition = false,
    subZone = false,
  } = props;
  const [status, setStatus] = React.useState();
  const [values, setValues] = React.useState({});
  const [types, setTypes] = React.useState();
  const [colors, setColors] = React.useState();
  const [typeAttr, setTypeAttr] = React.useState();
  const [valid, setValid] = React.useState();
  const [errors, setErrors] = React.useState({});
  const [parentZones, setParentZones] = React.useState();
  const [initiated, setInitiated] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const reset = () => {
    setStatus();
    setValues({});
    setValid({
      name: false,
      [`${level}_type_id`]: false,
    });
    setErrors({});
    setTypeAttr();
    setInitiated(false);
  };

  React.useEffect(() => {
    const initiate = async () => {
      try {
        // Fetch types
        const types = await getLov(
          level === levels.company
            ? "companyTypes"
            : level === levels.site
            ? "siteTypes"
            : level === levels.zone
            ? "zoneTypes"
            : "deviceTypes"
        );
        setTypes(types);
        // Fetch colors
        setColors(await getLov("colors"));
        // Fetch parent zones
        if (level === levels.zone) {
          setParentZones(
            await fetchGet(
              `companies/${props.companyId}/sites/${props.siteId}/zones`
            )
          );
        }
        // If edition fulfill the fields
        if (edition) {
          let newValues = {
            name: get(item, ["name"]),
            [`${level}_type_id`]: get(item, [`${level}_type_id`]),
            attributes: get(item, ["attributes"]),
            parent_zone_id: get(item, ["parent_zone_id"]),
            sys_color_id:
              get(item, ["color", "id"]) || get(item, ["type", "color", "id"]),
          };
          setValues(newValues);
          await fetchTypeAttr(get(item, [`${level}_type_id`]));
          validateValues(newValues);
        } else if (subZone) {
          setValues({
            parent_zone_id: get(item, ["id"]),
          });
        }
      } catch (e) {
        console.log(e);
      }
      setInitiated(true);
    };
    if (!initiated) initiate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiated]);

  const validateValues = async (values) => {
    let newValid = {
      name: false,
      [`${level}_type_id`]: false,
    };
    let newErrors = {};
    let fields = Object.keys(values);
    for (let i = 0; i < size(fields); i++) {
      if (fields[i] === "attributes") {
        for (let j = 0; j < size(get(values, ["attributes"])); j++) {
          [newValid, newErrors] = await validateField(
            values,
            newValid,
            newErrors,
            get(values, ["attributes", j, "id"])
          );
        }
      } else {
        [newValid, newErrors] = await validateField(
          values,
          newValid,
          newErrors,
          fields[i]
        );
      }
    }
    setValid(newValid);
    setErrors(newErrors);
  };

  // Initiate valid fields
  React.useEffect(() => {
    if (!edition) {
      setValid({
        name: false,
        [`${level}_type_id`]: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  const fetchTypeAttr = async (typeId) => {
    const attr = await fetchGet(
      level === levels.company
        ? `company_types/${typeId}/attributes`
        : level === levels.site
        ? `site_types/${typeId}/attributes`
        : level === levels.zone
        ? `zone_types/${typeId}/attributes`
        : `device_types/${typeId}/attributes`
    );
    // var new_attrs = []
    // if (level === "zone"){
    //   for (let i in attr){
    //     if (attr[i]["id"] != 20){
    //       new_attrs.push(attr[i])
    //     }
    //   }
    // }
    // setTypeAttr(new_attrs);
    setTypeAttr(attr);
  };

  const handleChange = (id, index, value) => {
    if (
      isNaN(parseInt(id)) &&
      id.includes("type_id") &&
      !isNaN(parseInt(value))
    ) {
      fetchTypeAttr(parseInt(value));
    }
    let newValues;
    if (!isNaN(parseInt(id))) {
      let newAttr = !get(values, ["attributes"])
        ? [{ id, value }]
        : size(
            filter(
              get(values, ["attributes"]),
              (attr) => get(attr, ["id"]) === id
            )
          ) === 0
        ? update(get(values, ["attributes"]), { $push: [{ id, value }] })
        : map(get(values, ["attributes"]), (attr) =>
            get(attr, ["id"]) === id ? { ...attr, value } : attr
          );
      newValues = update(values, {
        attributes: { $set: newAttr },
      });
    } else {
      newValues = update(values, {
        [id]: { $set: value },
      });
    }
    validation(newValues, id);
    setValues(newValues);
  };

  const validation = (newFormValues, field) => {
    debouncedValidation(newFormValues, valid, errors, field);
  };

  const debouncedValidation = React.useCallback(
    debounce(async (newFormValues, newValid, newErrors, field) => {
      if (field) {
        [newValid, newErrors] = await validateField(
          newFormValues,
          newValid,
          newErrors,
          field
        );
        setValid(newValid);
        setErrors(newErrors);
        return newValid;
      }
    }, 500),
    []
  );

  const validateField = async (newValues, newValid, newErrors, id) => {
    let isValid = false;
    let error;
    if (
      ["name"].includes(id) ||
      (isNaN(parseInt(id)) && id.includes("type_id"))
    ) {
      if (get(newValues, [id]) === "") {
        error = I18n.get("users_requiredField");
      } else {
        isValid = true;
      }
    } else {
      let attr = get(
        filter(typeAttr, (attr) => attr.id === id),
        0
      );
      if (
        (get(attr, ["required"]) && !get(newValues, ["id"])) ||
        get(newValues, ["id"]) === ""
      ) {
        isValid = false;
        error = I18n.get("users_requiredField");
      } else {
        isValid = true;
      }
    }
    return [
      update(newValid, {
        [id]: { $set: isValid },
      }),
      update(newErrors, {
        [id]: { $set: error },
      }),
    ];
  };

  const submit = async () => {
    setIsSubmitting(true);
    try {
      let url;
      if (level === levels.company) {
        url = `companies${!props.dialog ? `/${get(item, ["id"])}` : ""}`;
      } else if (level === levels.site) {
        url = `companies/${get(props, ["companyId"])}/sites${
          !props.dialog ? `/${get(item, ["id"])}` : ""
        }`;
      } else if (level === levels.zone) {
        url = `companies/${get(props, ["companyId"])}/sites/${get(props, [
          "siteId",
        ])}/zones${!props.dialog ? `/${get(item, ["id"])}` : ""}`;
      } else if (level === levels.device) {
        url = `companies/${get(props, ["companyId"])}/sites/${get(props, [
          "siteId",
        ])}/zones/${get(props, ["zoneId"])}/devices${
          !props.dialog ? `/${get(item, ["id"])}` : ""
        }`;
      }
      if (url && props.dialog) {
        // Creation
        await fetchPost(url, values);
      } else if (url) {
        // Edition
        await fetchPut(url, values);
      }

      await updateStoreCompanies();

      setStatus(success);
    } catch (e) {
      console.log(e);
      setStatus(error);
    } finally {
      setIsSubmitting(false);
    }
    refresh();
  };

  const updateStoreCompanies = async () => {
    const companies = await getAccessibleCompanies(
      get(user, ["permissions"]),
      get(user, ["assignedCompany"]),
      get(user, ["assignedSite"])
    );

    props.dispatch(
      setUser(
        update(user, {
          companies: { $set: companies },
        })
      )
    );
  };

  // Creation dialog view
  if (props.dialog) {
    return (
      <CreateDialog
        submit={submit}
        status={status}
        reset={reset}
        level={level}
        validForm={size(filter(valid, (v) => !v)) === 0}
        isSubmitting={isSubmitting}
        columns={
          size(
            filter(
              typeAttr,
              (attr) => attr.active && !attr.app_display_main_page
            )
          ) > 0
            ? 2
            : 1
        }
      >
        <CreateForm
          item={item}
          values={values}
          handleChange={handleChange}
          level={level}
          typeAttr={typeAttr}
          types={types}
          errors={errors}
          valid={valid}
          language={language}
          edition={false}
          colors={colors}
          subZone={subZone}
          parentZones={parentZones}
        />
      </CreateDialog>
    );
  }

  // Edition view
  return (
    <>
      <Grid item>
        <Grid container alignItems="center">
          <Grid item xs style={{ fontSize: "1.8em" }}>
            {get(item, ["name"])}
          </Grid>
          <Grid item>
            <Grid container>
              <Grid item>
                <CustomButton color="primary" onClick={props.cancel}>
                  {I18n.get("site_cancel")}
                </CustomButton>
              </Grid>
              <Grid item style={{ marginLeft: "10px" }}>
                <CustomButton
                  variant="contained"
                  color="primary"
                  disabled={size(filter(valid, (v) => !v)) > 0}
                  onClick={submit}
                >
                  {I18n.get("site_confirm")}
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item style={{ marginTop: "30px" }}>
        <Box
          style={{
            maxWidth:
              size(
                filter(
                  typeAttr,
                  (attr) => attr.active && !attr.app_display_main_page
                )
              ) > 0
                ? "650px"
                : "325px",
          }}
        >
          <CreateForm
            item={item}
            values={values}
            handleChange={handleChange}
            level={level}
            typeAttr={typeAttr}
            types={types}
            errors={errors}
            valid={valid}
            language={language}
            edition={true}
            colors={colors}
            subZone={subZone}
            parentZones={parentZones}
          />
        </Box>
      </Grid>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
    user: get(state, ["user"]),
  };
};

export default connect(mapStateToProps)(CreateContainer);
